import React from "react";
import { useLocation } from "react-router-dom";
import LoadingState from "./loading-state.jsx";
import SlowNetwork from "./slow-network.jsx";
import ErrorPage from "./error-page.jsx";
import PermissionDenied from "./permission-denied.jsx";
import SessionExpired from "./session-expired.jsx";
import SuccessState from "./success-state.jsx";
import FormValidation from "./form-validation.jsx";

const eventName = "hallef-app-state";
export function showAppState(state, details = {}) {
  window.dispatchEvent(new CustomEvent(eventName, { detail: { state, ...details } }));
}

export async function apiFetch(url, options) {
  showAppState("request-start");
  try {
    const response = await fetch(url, options);
    const authRequest = /\/auth\/(login|register)(?:\?|$)/.test(String(url));
    if (response.status === 401 && !authRequest) {
      localStorage.removeItem("hallef-admin-token");
      localStorage.removeItem("hallef-admin-auth");
      localStorage.removeItem("hallef-user-role");
      showAppState("session-expired");
    } else if (response.status === 403) {
      showAppState("permission-denied");
    } else if (response.status >= 500) {
      showAppState("error");
    }
    // Admin mutations must not update local data after a rejected request.
    if (!response.ok && window.location.pathname === "/admin") {
      if (response.status !== 401 && response.status !== 403) showAppState("error");
      throw new Error(`Request failed (${response.status})`);
    }
    return response;
  } catch (error) {
    if (navigator.onLine && error instanceof TypeError) showAppState("error");
    throw error;
  } finally {
    showAppState("request-end");
  }
}

export class AppErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() { return this.state.hasError ? <ErrorPage /> : this.props.children; }
}

export function AppStateBoundary({ children }) {
  const location = useLocation();
  const [pending, setPending] = React.useState(0);
  const [slow, setSlow] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  const isLoading = pending > 0;

  React.useLayoutEffect(() => {
    const receive = ({ detail }) => {
      if (detail.state === "request-start") setPending((count) => count + 1);
      else if (detail.state === "request-end") setPending((count) => Math.max(0, count - 1));
      else setNotice(detail);
    };
    window.addEventListener(eventName, receive);
    return () => window.removeEventListener(eventName, receive);
  }, []);

  React.useEffect(() => { setNotice(null); }, [location.key]);
  React.useEffect(() => {
    if (!isLoading) { setSlow(false); return undefined; }
    const timer = window.setTimeout(() => setSlow(true), 8000);
    return () => window.clearTimeout(timer);
  }, [isLoading]);

  let screen = null;
  if (notice?.state === "error") screen = <ErrorPage />;
  else if (notice?.state === "permission-denied") screen = <PermissionDenied />;
  else if (notice?.state === "session-expired") screen = <SessionExpired />;
  else if (notice?.state === "success") screen = <SuccessState message={notice.message} onContinue={() => setNotice(null)} />;
  else if (notice?.state === "form-validation") screen = <FormValidation errors={notice.errors} onBack={() => setNotice(null)} />;
  else if (pending) screen = slow ? <SlowNetwork /> : <LoadingState />;

  return <><div hidden={Boolean(screen)}>{children}</div>{screen && <div role="status" aria-live="polite">{screen}</div>}</>;
}
